import { prettyErrorDescription } from 'constants/errors'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import formServices from 'services/form-builder-services'
import toast from 'react-hot-toast'
import { Label } from 'theme/theme'
import Input from "components/input";
import { usePopupContext } from 'hooks/use-main-cass'
import { useContext, useEffect } from 'react'
import { LoaderContext } from 'contexts/loader-context'
import ProfileService from 'services/profile-services'
import { useSession } from 'contexts/session-context'
import { dlTrackError } from 'components/dataLayer/dataLayer'
import { AxiosError } from 'axios'
import { useLocalStorage } from 'react-use'

interface IProps {
  defaultValues: { instagram?: string }
}

const EditAccounts = ({ defaultValues }: IProps) => {
  const { closePopup } = usePopupContext()
  const { setLoad } = useContext(LoaderContext)
  const [{ data: session }, { setUser }] = useSession()
  const [state, setState] = useLocalStorage('state', {})
  const { register, handleSubmit, setValue, formState: { errors } } = useForm({
    resolver: yupResolver(formServices.formGenerateSchema(["instagram"])),
    criteriaMode: 'firstError',
    reValidateMode: 'onSubmit',
    mode: 'all'
  })

  useEffect(() => {
    if (defaultValues) {
      setValue('instagram', defaultValues?.instagram)
    }
  }, [defaultValues])

  const handleError = (error: AxiosError) => {
    if (error?.isAxiosError) {
      const { code: errorCode, message: errorMessage } = error?.response?.data ?? {}
      dlTrackError({
        error_type: 'login',
        error_code: { errorCode },
        error_message: { errorMessage },
        section: 'login'
      })
      toast.error(prettyErrorDescription(errorCode), { className: 'toast__error' })
    }
  }

  async function onFormSubmit(data: any) {
    setLoad(true)
    try {
      if (!data.instagram.includes('@') && data.instagram) {
        data.instagram = `@${data.instagram}`
      }
      const result = await ProfileService.instagram(data.instagram, session)
      setState({ ...state, user: result?.data })
      setUser(result?.data)
      toast.success('Los cambios se guardaron con éxito', { className: 'toast__success' })
      closePopup()
    } catch (err) {
      handleError(err)
    } finally {
      setLoad(false)
    }
  }

  const onInvalid = (errors: Record<string, any>) => {
    if (errors) {
      const errorsKeys = Object.values(errors)
      toast.error(errorsKeys[0]?.message, { className: 'toast__error' })
    }
  };

  return (
    <div className="pb-4">
      <h2 className="form__sign-in__title">Agrega tu cuenta</h2>
      <form onSubmit={handleSubmit(onFormSubmit, onInvalid)} className='space-y-4'>
        <div className={`gap-2 flex flex-col input-container ${errors?.phoneNumber?.message ? 'error-input' : ''}`}>
          <div className='flex items-center gap-2'>
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3.37598 0.21875H7.62402C8.1709 0.21875 8.67383 0.355469 9.13281 0.628906C9.60156 0.902344 9.97266 1.27344 10.2461 1.74219C10.5195 2.20117 10.6562 2.7041 10.6562 3.25098V7.49902C10.6562 8.0459 10.5195 8.55371 10.2461 9.02246C9.97266 9.48145 9.60156 9.84766 9.13281 10.1211C8.67383 10.3945 8.1709 10.5312 7.62402 10.5312H3.37598C2.8291 10.5312 2.32129 10.3945 1.85254 10.1211C1.39355 9.84766 1.02734 9.48145 0.753906 9.02246C0.480469 8.55371 0.34375 8.0459 0.34375 7.49902V3.25098C0.34375 2.7041 0.480469 2.20117 0.753906 1.74219C1.02734 1.27344 1.39355 0.902344 1.85254 0.628906C2.32129 0.355469 2.8291 0.21875 3.37598 0.21875ZM3.37598 1.15625C2.99512 1.15625 2.64355 1.24902 2.32129 1.43457C1.99902 1.62012 1.74512 1.87402 1.55957 2.19629C1.37402 2.51855 1.28125 2.87012 1.28125 3.25098V7.49902C1.28125 7.87988 1.37402 8.23145 1.55957 8.55371C1.74512 8.87598 1.99902 9.12988 2.32129 9.31543C2.64355 9.50098 2.99512 9.59375 3.37598 9.59375H7.62402C8.00488 9.59375 8.35645 9.50098 8.67871 9.31543C9.00098 9.12988 9.25488 8.87598 9.44043 8.55371C9.62598 8.23145 9.71875 7.87988 9.71875 7.49902V3.25098C9.71875 2.87012 9.62598 2.51855 9.44043 2.19629C9.25488 1.87402 9.00098 1.62012 8.67871 1.43457C8.35645 1.24902 8.00488 1.15625 7.62402 1.15625H3.37598ZM8.26855 2.18164C8.38574 2.18164 8.4834 2.22559 8.56152 2.31348C8.64941 2.3916 8.69336 2.48926 8.69336 2.60645C8.69336 2.72363 8.64941 2.82617 8.56152 2.91406C8.4834 2.99219 8.38574 3.03125 8.26855 3.03125C8.15137 3.03125 8.04883 2.99219 7.96094 2.91406C7.88281 2.82617 7.84375 2.72363 7.84375 2.60645C7.84375 2.48926 7.88281 2.3916 7.96094 2.31348C8.04883 2.22559 8.15137 2.18164 8.26855 2.18164ZM5.5 2.5625C6.00781 2.5625 6.47656 2.68945 6.90625 2.94336C7.33594 3.19727 7.67773 3.53906 7.93164 3.96875C8.18555 4.39844 8.3125 4.86719 8.3125 5.375C8.3125 5.88281 8.18555 6.35156 7.93164 6.78125C7.67773 7.21094 7.33594 7.55273 6.90625 7.80664C6.47656 8.06055 6.00781 8.1875 5.5 8.1875C4.99219 8.1875 4.52344 8.06055 4.09375 7.80664C3.66406 7.55273 3.32227 7.21094 3.06836 6.78125C2.81445 6.35156 2.6875 5.88281 2.6875 5.375C2.6875 4.86719 2.81445 4.39844 3.06836 3.96875C3.32227 3.53906 3.66406 3.19727 4.09375 2.94336C4.52344 2.68945 4.99219 2.5625 5.5 2.5625ZM5.5 3.5C5.1582 3.5 4.84082 3.58301 4.54785 3.74902C4.26465 3.91504 4.04004 4.14453 3.87402 4.4375C3.70801 4.7207 3.625 5.0332 3.625 5.375C3.625 5.7168 3.70801 6.03418 3.87402 6.32715C4.04004 6.61035 4.26465 6.83496 4.54785 7.00098C4.84082 7.16699 5.1582 7.25 5.5 7.25C5.8418 7.25 6.1543 7.16699 6.4375 7.00098C6.73047 6.83496 6.95996 6.61035 7.12598 6.32715C7.29199 6.03418 7.375 5.7168 7.375 5.375C7.375 5.0332 7.29199 4.7207 7.12598 4.4375C6.95996 4.14453 6.73047 3.91504 6.4375 3.74902C6.1543 3.58301 5.8418 3.5 5.5 3.5Z" fill="white" />
            </svg>
            <Label className="text-start text-2xl font-bold uppercase input-label">
              Tu cuenta de instagram
            </Label>
          </div>
          <Input
            className="text-center saira font-bold text-sm"
            type="text"
            placeholder="@usuario"
            name="instagram"
            {...register("instagram")}
          />
        </div>
        <div className='space-y-2'>
          <button type="submit" className="form__sign-in__btn form__sign-in__btn__main">
            Guardar cambios
          </button>
        </div>
      </form>
    </div>
  )
}

export default EditAccounts
